// src/pages/Settings/communication/Notifications.js
import React, { useEffect, useState } from 'react';
import { get, put } from 'helpers/api_helper';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Badge,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { showSuccessAlert, showErroeAlert } from 'pages/utils/alertMessages';
import cookieHelper from 'helpers/getCookieData';
import {
  RiNotification3Line,
  RiMailLine,
  RiWhatsappLine,
  RiTimeLine,
  RiSunLine,
  RiMoonLine,
  RiShieldCheckLine,
  RiServerLine,
  RiQrCodeLine,
  RiCheckboxCircleLine,
  RiAlertLine,
  RiSaveLine,
  RiRefreshLine,
} from 'react-icons/ri';
import { Link } from 'react-router-dom';

const Notifications = () => {
  const { t } = useTranslation();
  const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
  const clinicId = clinic?.id || 8;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Settings State
  const [settings, setSettings] = useState({
    clinic_id: clinicId,
    clinic_name: clinic?.clinic_name || '',
    email_notifications: true,
    whatsapp_notifications: true,
    email_reminders: true,
    whatsapp_reminders: true,
    email_daily_briefing: true,
    whatsapp_daily_briefing: true,
    notification_settings: {},
    email_credential_source: 'GLOBAL',
    whatsapp_credential_source: 'GLOBAL',
    tenant_smtp_configured: false,
    tenant_whatsapp_configured: false,
    global_smtp_active: true,
    global_whatsapp_active: true,
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await get(`/clinics/${clinicId}/notification-settings`);
      if (res?.success && res?.body) {
        setSettings((prev) => ({
          ...prev,
          ...res.body,
        }));
      }
    } catch (err) {
      console.warn('Error fetching notification settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [clinicId]);

  const handleToggle = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const res = await put(`/clinics/${clinicId}/notification-settings`, {
        email_notifications: settings.email_notifications,
        whatsapp_notifications: settings.whatsapp_notifications,
        email_reminders: settings.email_reminders,
        whatsapp_reminders: settings.whatsapp_reminders,
        email_daily_briefing: settings.email_daily_briefing,
        whatsapp_daily_briefing: settings.whatsapp_daily_briefing,
      });

      if (res?.success) {
        showSuccessAlert(t('Notification & Reminder settings updated successfully!'));
        if (res.body) {
          setSettings((prev) => ({ ...prev, ...res.body }));
        }
      } else {
        showErroeAlert(res?.message || t('Failed to update notification settings.'));
      }
    } catch (err) {
      showErroeAlert(t('Error updating settings: ') + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-5 text-center">
        <Spinner color="primary" size="md" />
        <p className="mt-2 text-muted font-size-13">{t('Loading Notification Rules & Toggles...')}</p>
      </div>
    );
  }

  return (
    <div className="notification-rules-container">
      {/* Priority Resolution Status Banner */}
      <Card className="border shadow-sm mb-4 bg-light">
        <CardBody className="p-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle p-2 bg-primary text-white font-size-18 d-flex align-items-center justify-content-center"
                style={{ width: 40, height: 40 }}
              >
                <RiNotification3Line />
              </div>
              <div>
                <h6 className="font-weight-bold text-dark mb-0">
                  {t('Clinic Communication & Priority Status')}
                </h6>
                <p className="text-muted font-size-12 mb-0">
                  {t('Dispatches verify your clinic toggles below before sending. Credentials follow priority: ')}
                  <strong className="text-dark">Tenant Custom Credentials</strong> →{' '}
                  <strong className="text-primary">Super Admin Global Credentials</strong> →{' '}
                  <strong className="text-danger">Skip & Log</strong>.
                </p>
              </div>
            </div>

            {/* Live Badges */}
            <div className="d-flex align-items-center gap-2">
              <div className="border bg-white rounded px-2.5 py-1.5 font-size-12 d-flex align-items-center gap-1.5 shadow-2xs">
                <RiMailLine className="text-primary font-size-14" />
                <span className="text-muted">{t('Email Provider:')}</span>
                {settings.email_credential_source === 'TENANT' ? (
                  <Badge color="info" className="font-size-11">
                    {t('Custom Clinic SMTP')}
                  </Badge>
                ) : (
                  <Badge color="primary" className="font-size-11">
                    {t('Platform Global SMTP')}
                  </Badge>
                )}
              </div>

              <div className="border bg-white rounded px-2.5 py-1.5 font-size-12 d-flex align-items-center gap-1.5 shadow-2xs">
                <RiWhatsappLine className="text-success font-size-14" />
                <span className="text-muted">{t('WhatsApp Provider:')}</span>
                {settings.whatsapp_credential_source === 'TENANT' ? (
                  <Badge color="success" className="font-size-11">
                    {t('Custom Clinic WhatsApp')}
                  </Badge>
                ) : (
                  <Badge color="primary" className="font-size-11">
                    {t('Platform Global API')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Form onSubmit={handleSave}>
        <Row className="g-4">
          {/* 1. NOTIFICATION TOGGLES */}
          <Col md={6}>
            <Card className="border shadow-sm h-100">
              <CardHeader className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center gap-1.5">
                      <RiNotification3Line className="text-primary font-size-16" />
                      {t('Instant Notifications')}
                    </h6>
                    <small className="text-muted">
                      {t('Real-time alerts triggered on appointment bookings, cancellations & updates')}
                    </small>
                  </div>
                </div>
              </CardHeader>

              <CardBody className="p-3">
                {/* Email Notifications Toggle */}
                <div className="d-flex justify-content-between align-items-center p-3 border rounded mb-3 bg-light">
                  <div className="d-flex align-items-center gap-2.5">
                    <div className="p-2 rounded bg-white border text-primary font-size-18">
                      <RiMailLine />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-dark mb-0">{t('Email Notifications')}</h6>
                      <small className="text-muted d-block">
                        {t('Send email confirmations to patients and doctors upon appointment creation or cancellation.')}
                      </small>
                    </div>
                  </div>
                  <div className="form-check form-switch ms-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="emailNotificationsToggle"
                      checked={settings.email_notifications}
                      onChange={() => handleToggle('email_notifications')}
                    />
                    <Label className="form-check-label font-weight-bold font-size-12" for="emailNotificationsToggle">
                      {settings.email_notifications ? (
                        <span className="text-success">{t('ON')}</span>
                      ) : (
                        <span className="text-danger">{t('OFF')}</span>
                      )}
                    </Label>
                  </div>
                </div>

                {/* WhatsApp Notifications Toggle */}
                <div className="d-flex justify-content-between align-items-center p-3 border rounded bg-light">
                  <div className="d-flex align-items-center gap-2.5">
                    <div className="p-2 rounded bg-white border text-success font-size-18">
                      <RiWhatsappLine />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-dark mb-0">{t('WhatsApp Notifications')}</h6>
                      <small className="text-muted d-block">
                        {t('Dispatch instant WhatsApp alerts for newly confirmed or rescheduled appointments.')}
                      </small>
                    </div>
                  </div>
                  <div className="form-check form-switch ms-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="waNotificationsToggle"
                      checked={settings.whatsapp_notifications}
                      onChange={() => handleToggle('whatsapp_notifications')}
                    />
                    <Label className="form-check-label font-weight-bold font-size-12" for="waNotificationsToggle">
                      {settings.whatsapp_notifications ? (
                        <span className="text-success">{t('ON')}</span>
                      ) : (
                        <span className="text-danger">{t('OFF')}</span>
                      )}
                    </Label>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* 2. REMINDER TOGGLES */}
          <Col md={6}>
            <Card className="border shadow-sm h-100">
              <CardHeader className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center gap-1.5">
                      <RiTimeLine className="text-warning font-size-16" />
                      {t('Automated Reminders')}
                    </h6>
                    <small className="text-muted">
                      {t('Scheduled reminders for upcoming appointments, follow-ups & outstanding balances')}
                    </small>
                  </div>
                </div>
              </CardHeader>

              <CardBody className="p-3">
                {/* Email Reminders Toggle */}
                <div className="d-flex justify-content-between align-items-center p-3 border rounded mb-3 bg-light">
                  <div className="d-flex align-items-center gap-2.5">
                    <div className="p-2 rounded bg-white border text-warning font-size-18">
                      <RiMailLine />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-dark mb-0">{t('Email Reminders')}</h6>
                      <small className="text-muted d-block">
                        {t('Send reminder emails 24 hours prior to appointment slots and pending follow-ups.')}
                      </small>
                    </div>
                  </div>
                  <div className="form-check form-switch ms-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="emailRemindersToggle"
                      checked={settings.email_reminders}
                      onChange={() => handleToggle('email_reminders')}
                    />
                    <Label className="form-check-label font-weight-bold font-size-12" for="emailRemindersToggle">
                      {settings.email_reminders ? (
                        <span className="text-success">{t('ON')}</span>
                      ) : (
                        <span className="text-danger">{t('OFF')}</span>
                      )}
                    </Label>
                  </div>
                </div>

                {/* WhatsApp Reminders Toggle */}
                <div className="d-flex justify-content-between align-items-center p-3 border rounded bg-light">
                  <div className="d-flex align-items-center gap-2.5">
                    <div className="p-2 rounded bg-white border text-success font-size-18">
                      <RiWhatsappLine />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-dark mb-0">{t('WhatsApp Reminders')}</h6>
                      <small className="text-muted d-block">
                        {t('Send WhatsApp reminder messages directly to patients prior to their scheduled visits.')}
                      </small>
                    </div>
                  </div>
                  <div className="form-check form-switch ms-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="waRemindersToggle"
                      checked={settings.whatsapp_reminders}
                      onChange={() => handleToggle('whatsapp_reminders')}
                    />
                    <Label className="form-check-label font-weight-bold font-size-12" for="waRemindersToggle">
                      {settings.whatsapp_reminders ? (
                        <span className="text-success">{t('ON')}</span>
                      ) : (
                        <span className="text-danger">{t('OFF')}</span>
                      )}
                    </Label>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* 3. DAILY BRIEFINGS & SUMMARIES */}
          <Col md={12}>
            <Card className="border shadow-sm">
              <CardHeader className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center gap-1.5">
                      <RiSunLine className="text-info font-size-16" />
                      {t('Clinical Daily Briefings & EOD Summaries')}
                    </h6>
                    <small className="text-muted">
                      {t('Automated morning agendas and midnight financial & operational summaries')}
                    </small>
                  </div>
                </div>
              </CardHeader>

              <CardBody className="p-3">
                <Row className="g-3">
                  <Col md={6}>
                    <div className="p-3 border rounded bg-light d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2.5">
                        <div className="p-2 rounded bg-white border text-primary font-size-18">
                          <RiSunLine />
                        </div>
                        <div>
                          <h6 className="font-weight-bold text-dark mb-0">{t('Email Daily Briefing')}</h6>
                          <small className="text-muted">{t('08:00 AM Morning schedule & patient lineup email')}</small>
                        </div>
                      </div>
                      <div className="form-check form-switch ms-3">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="emailBriefingToggle"
                          checked={settings.email_daily_briefing}
                          onChange={() => handleToggle('email_daily_briefing')}
                        />
                        <Label className="form-check-label font-weight-bold font-size-12" for="emailBriefingToggle">
                          {settings.email_daily_briefing ? (
                            <span className="text-success">{t('ON')}</span>
                          ) : (
                            <span className="text-danger">{t('OFF')}</span>
                          )}
                        </Label>
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="p-3 border rounded bg-light d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2.5">
                        <div className="p-2 rounded bg-white border text-success font-size-18">
                          <RiWhatsappLine />
                        </div>
                        <div>
                          <h6 className="font-weight-bold text-dark mb-0">{t('WhatsApp Daily Briefing')}</h6>
                          <small className="text-muted">{t('08:00 AM WhatsApp schedule delivered to doctor mobile')}</small>
                        </div>
                      </div>
                      <div className="form-check form-switch ms-3">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="waBriefingToggle"
                          checked={settings.whatsapp_daily_briefing}
                          onChange={() => handleToggle('whatsapp_daily_briefing')}
                        />
                        <Label className="form-check-label font-weight-bold font-size-12" for="waBriefingToggle">
                          {settings.whatsapp_daily_briefing ? (
                            <span className="text-success">{t('ON')}</span>
                          ) : (
                            <span className="text-danger">{t('OFF')}</span>
                          )}
                        </Label>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Save Bar */}
        <div className="d-flex justify-content-between align-items-center mt-4 p-3 bg-white border rounded shadow-sm">
          <div className="d-flex align-items-center gap-3">
            <Link to="/settings/communication/email-config" className="btn btn-sm btn-outline-secondary font-size-12">
              <RiServerLine className="me-1" /> {t('Configure Custom SMTP')}
            </Link>
            <Link to="/settings/communication/whatsapp-config" className="btn btn-sm btn-outline-secondary font-size-12">
              <RiQrCodeLine className="me-1" /> {t('Configure WhatsApp Gateway')}
            </Link>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              type="button"
              color="light"
              onClick={fetchSettings}
              disabled={saving}
            >
              <RiRefreshLine className="me-1" /> {t('Reset')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={saving}
              className="px-4"
            >
              {saving ? <Spinner size="sm" className="me-1" /> : <RiSaveLine className="me-1" />}
              {t('Save Notification Settings')}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default Notifications;
